import { FormColumns, FormLabel } from "components/form";
import { FormField, FormItem, FormMessage } from "ui/components/form";
import { Form } from "react-crud-form";
import { FormActions } from "components/form/actions";
import EmployeeInput from "components/employee/input";
import { PermissionControlledInput } from "permissions/components/input";
import { Switch } from "ui/components/switch";
import React from "react";
import { jobEmployeeSchema } from "./schema";
import { useJobEmployeeForm } from "./useJobEmployeeForm";
import { useJob } from "../../../context";


const JobEmployeeForm = ({ jobId, employeeId, onSuccess }) => {
  const { type, departments, isDepartmentRequired } = useJob();

  const employeeFilters = {
    ...(isDepartmentRequired
      ? {
        department: {
          _in: departments,
        },
      }
      : {}),
  };

  const { form, requiredFields, handleCreate, handleRead, handleUpdate } =
    useJobEmployeeForm({ jobId, employeeId, onSuccess });

  return (
    <Form
      form={form}
      create={handleCreate}
      read={handleRead}
      update={handleUpdate}
      itemId={employeeId || "+"}
      schema={jobEmployeeSchema}
    >
      {({ isCreating, createItem, isNewItem, isUpdating, updateItem, isDirty }) => (
        <div className="space-y-6">
          {/* Employee */}
          <FormField
            control={form.control}
            name="employee"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:PC,JC,SC,AM"}
                  collection="jobs_employees"
                  type="form"
                  field={field.name}
                >
                  <FormLabel required={requiredFields.includes(field.name)}>
                    Employee
                  </FormLabel>
                  <EmployeeInput
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!isNewItem}
                    filters={employeeFilters}
                    info={
                      isDepartmentRequired &&
                      "You can only add employees whose departments are included in the budget."
                    }
                  />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Reviewer */}
          <FormField
            control={form.control}
            name="reviewer"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:PC,JC,SC,AM"}
                  collection="jobs_employees"
                  type="form"
                  field={field.name}
                >
                  <FormLabel required={requiredFields.includes(field.name)}>
                    Reviewer
                  </FormLabel>
                  <EmployeeInput
                    value={field.value}
                    onChange={field.onChange}
                  />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormColumns>
            {/* Allow Logging */}
            <FormField
              control={form.control}
              name="is_logging_restricted"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:PC,JC,SC,AM"}
                    collection="jobs_employees"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Restrict Logging
                    </FormLabel>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </PermissionControlledInput>
                </FormItem>
              )}
            />

            {/* Is Shadow — only for dedicated jobs */}
            {type === "dedicated" && (
              <FormField
                control={form.control}
                name="is_shadow"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2">
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:PC,JC,SC,AM"}
                      collection="jobs_employees"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Shadow
                      </FormLabel>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </PermissionControlledInput>
                  </FormItem>
                )}
              />
            )}
          </FormColumns>

          <FormActions
            rule={isNewItem ? "create:JC,SC,AM" : "update:PC,JC,SC,AM"}
            collection="jobs_employees"
            isNewItem={isNewItem}
            isCreating={isCreating}
            createItem={createItem}
            isDirty={isDirty}
            updateItem={updateItem}
            isUpdating={isUpdating}
          />
        </div>
      )}
    </Form>
  );
};

export default JobEmployeeForm;
